import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer } from "./EffectorBase";
import { CustomEventTypes, EasingFunctionTypes, EasingTypeToFunctionMap, PropertyTypes, SaveModificationTypes } from "../../../globals";
import Vector3Property from "../../../components/properties/Vector3Property";
import NumberProperty from "../../../components/properties/NumberProperty";
import DropdownProperty from "../../../components/properties/DropdownProperty";
import Engine from '../../Engine';
import ProjectSaver from '../../ProjectSaver';
import { assert } from '../../utilities/ErrorHandling';
import TransformUtils from '../../utilities/TransformUtils';
import MathUtils from '../../utilities/MathUtils';
import { TimelineFragment } from '../TimelineFragment';
import { areArraysEqual } from '../../utilities/Misc';

const tempQuaternion = new THREE.Quaternion();
const normalizedWorldAxis = new THREE.Vector3();
const parentWorldRotation = new THREE.Quaternion();
const parentWorldRotationMatrix = new THREE.Matrix4();
const rotatedNormalizedWorldAxis = new THREE.Vector3();

enum RotationMode {
	aroundSelf = "aroundSelf",
	aroundPivot ="aroundPivot",
}

export default class EffectorTransformRotateDeprecated extends EffectorBase<EffectorTransformRotateDeprecated>
{
	axis: THREE.Vector3;
	angle: number;
	easing: EasingFunctionTypes;
	rotationMode: RotationMode;
	rotationPivot: THREE.Vector3;
	deltaAngle: number;

	constructor(fragment: TimelineFragment)
	{
		super(fragment);

		this.axis = new THREE.Vector3(0, 0, 0);
		this.angle = 0.0;
		this.easing = EasingFunctionTypes.Linear;
		this.rotationMode = RotationMode.aroundSelf;
		this.rotationPivot = new THREE.Vector3(0, 0, 0);
		this.deltaAngle = 0.0;
	}

	save = () =>
	{
		const output = {
			axis: { x: this.axis.x, y: this.axis.y, z: this.axis.z },
			angle: this.angle,
			easing: this.easing,
			rotationMode: this.rotationMode,
			rotationPivot: { x: this.rotationPivot.x, y: this.rotationPivot.y, z: this.rotationPivot.z },
		};

		return output;
	};

	load = (savedData) =>
	{
		if (savedData.rotationAmount)//backwards comptability, thanks http://www.andre-gaschler.com/rotationconverter/
		{
			const quat = new THREE.Quaternion().setFromEuler(new THREE.Euler(savedData.rotationAmount.x * Math.PI / 180, savedData.rotationAmount.y * Math.PI / 180, savedData.rotationAmount.z * Math.PI / 180));
			const axisAngle = MathUtils.quaternionToAxisAngle(quat);
			this.angle = axisAngle.angle;
			this.axis.set(axisAngle.axis.x, axisAngle.axis.y, axisAngle.axis.z);
		}
		else
		{
			this.axis.set(savedData.axis.x, savedData.axis.y, savedData.axis.z);
			this.angle = savedData.angle;
			this.rotationMode = savedData.rotationMode;
			this.rotationPivot.set(savedData.rotationPivot.x, savedData.rotationPivot.y, savedData.rotationPivot.z);
		}
		if (savedData.easing) this.easing = savedData.easing.charAt(0).toLowerCase() + savedData.easing.slice(1);
	};

	copyValues = (sourceEffector: EffectorTransformRotateDeprecated) =>
	{
		this.angle = sourceEffector.angle;
		this.axis.copy(sourceEffector.axis);
		this.easing = sourceEffector.easing;
		this.rotationMode = sourceEffector.rotationMode;
		this.rotationPivot.copy(sourceEffector.rotationPivot);
	}

	reverse = () =>
	{
		assert(!this.fragment.timeline.updated);
		this.deltaAngle = this.angle / 180.0 * Math.PI;
		normalizedWorldAxis.copy(this.axis).normalize();

		this.forEachTargetObject((targetObject) =>
		{
			if (this.rotationMode === RotationMode.aroundPivot)
			{
				TransformUtils.rotateAround(targetObject, this.rotationPivot, normalizedWorldAxis, this.deltaAngle);
			}
			else
			{
				if (targetObject.parent && targetObject.parent.parent!.type !== 'Scene') //objct isn't a direct child of the scene root
				{
					targetObject.parent.getWorldQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.makeRotationFromQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.invert();
					rotatedNormalizedWorldAxis.copy(normalizedWorldAxis).applyMatrix4(parentWorldRotationMatrix);
					tempQuaternion.setFromAxisAngle(rotatedNormalizedWorldAxis, this.deltaAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
				else
				{
					tempQuaternion.setFromAxisAngle(normalizedWorldAxis, this.deltaAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
			}
		});

		this.angle *= -1;

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	setAngle = (value : number) =>
	{
		this.angle = value;
	}

	setAxis = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.axis.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.axis.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.axis.z = z;
	}

	setRotationMode = (mode: RotationMode) =>
	{
		this.rotationMode = mode;
		if (this.rotationMode === RotationMode.aroundPivot) this.rotationPivot.set(0, 0, 0);
	}

	setRotationPivot = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.rotationPivot.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.rotationPivot.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.rotationPivot.z = z;
	}

	resetState = () =>
	{
		if (!this.fragment.previousTime) return;

		const inverseAngle = -this.deltaAngle;
		normalizedWorldAxis.copy(this.axis).normalize();

		this.forEachTargetObject((targetObject) =>
		{
			if (this.rotationMode === RotationMode.aroundPivot)
			{
				TransformUtils.rotateAround(targetObject, this.rotationPivot, normalizedWorldAxis, inverseAngle);
			}
			else
			{
				if (targetObject.parent && targetObject.parent.parent!.type !== 'Scene') //objct isn't a direct child of the scene root
				{
					targetObject.parent.getWorldQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.makeRotationFromQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.invert();
					rotatedNormalizedWorldAxis.copy(normalizedWorldAxis).applyMatrix4(parentWorldRotationMatrix);
					tempQuaternion.setFromAxisAngle(rotatedNormalizedWorldAxis, inverseAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
				else
				{
					tempQuaternion.setFromAxisAngle(normalizedWorldAxis, inverseAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
			}
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	update = (fragmentTime : number ) =>
	{
		if (!fragmentTime) return;

		const t = EasingTypeToFunctionMap.get(this.easing)!(Math.min(fragmentTime, 1.0));
		this.deltaAngle = (this.angle * t / 180.0 * Math.PI);
		normalizedWorldAxis.copy(this.axis).normalize();

		this.forEachTargetObject((targetObject) =>
		{
			if (this.rotationMode === RotationMode.aroundPivot)
			{
				TransformUtils.rotateAround(targetObject, this.rotationPivot, normalizedWorldAxis, this.deltaAngle);
			}
			else
			{
				if (targetObject.parent && targetObject.parent.parent!.type !== 'Scene') //objct isn't a direct child of the scene root
				{
					targetObject.parent.getWorldQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.makeRotationFromQuaternion(parentWorldRotation);
					parentWorldRotationMatrix.invert();
					rotatedNormalizedWorldAxis.copy(normalizedWorldAxis).applyMatrix4(parentWorldRotationMatrix);
					tempQuaternion.setFromAxisAngle(rotatedNormalizedWorldAxis, this.deltaAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
				else
				{
					tempQuaternion.setFromAxisAngle(normalizedWorldAxis, this.deltaAngle);
					targetObject.quaternion.premultiply(tempQuaternion);
				}
			}
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	onAdded = () =>
	{
		for (const object of this.targetObjects())
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Rotation);
		}
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Angle",
				PropertyTypes.Number,
				<NumberProperty
					value={this.angle}
					min={0.0}
					step={45}
					decimals={1}
				/>,
				() => {return this.angle;},
				(v) => {this.setAngle(Number(v));},
			),
			new EffectorPropertyDrawer(
				"Axis",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.axis.x, this.axis.y, this.axis.z]}
					renderChildrenInSeparateRows = {true}
				/>,
				() => {return [this.axis.x, this.axis.y, this.axis.z];},
				(v) => {this.setAxis(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
			new EffectorPropertyDrawer(
				"Rotation Mode",
				PropertyTypes.DropDown,
				<DropdownProperty
					data-name={"property-rotation-mode"}
					value={this.rotationMode}
					options={[
						{ display: "Around Self", value: RotationMode.aroundSelf },
						{ display: "Around Pivot", value: RotationMode.aroundPivot },
					]}
				/>,
				() => {return this.rotationMode;},
				(v) => {this.setRotationMode(v);},
			),
			...(this.rotationMode === RotationMode.aroundPivot ?
				[new EffectorPropertyDrawer(
					"Rotation Pivot",
					PropertyTypes.Vector3,
					<Vector3Property
						value={[this.rotationPivot.x, this.rotationPivot.y, this.rotationPivot.z]}
						step={0.1}
						renderChildrenInSeparateRows={true}
						decimals={3}
					/>,
					() => {return [this.rotationPivot.x, this.rotationPivot.y, this.rotationPivot.z];},
					(v) => {this.setRotationPivot(v.x, v.y, v.z);},
					(a, b) => {return areArraysEqual(a, b);},
				), ] : []),
		];
	}
}
